import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import { unwrapBettingDoc } from "../utils/joinedBetsHelpers";
import { unwrapSlipResponse } from "../utils/bettingDeskHelpers";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

export const fetchBettingSlip = createAsyncThunk(
  "bettingDesk/fetchSlip",
  async ({ userId, betId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/betting/${userId}/${betId}`);
      return unwrapSlipResponse(res.data);
    } catch (error) {
      if (error.response?.status === 404) return null;
      return rejectWithValue(apiMessage(error, "Could not load your slip"));
    }
  }
);

export const addSelectionToSlip = createAsyncThunk(
  "bettingDesk/addSelection",
  async ({ userId, betId, fixtureId, selectedOdd }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/betting/add", {
        userId,
        betId,
        fixtureId,
        selectedOdd,
      });
      return unwrapBettingDoc(res.data);
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not add selection"));
    }
  }
);

export const removeSelectionFromSlip = createAsyncThunk(
  "bettingDesk/removeSelection",
  async ({ userId, betId, fixtureId }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/betting/remove", {
        userId,
        betId,
        fixtureId,
      });
      return unwrapBettingDoc(res.data);
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not remove selection"));
    }
  }
);

export const placeBetTicket = createAsyncThunk(
  "bettingDesk/placeBet",
  async ({ userId, betId, amountBet, cashType = "Cash" }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/betting/make", {
        userId,
        betId,
        amountBet,
        cashType,
      });
      return unwrapBettingDoc(res.data);
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not place bet"));
    }
  }
);

const bettingDeskSlice = createSlice({
  name: "bettingDesk",
  initialState: {
    slip: null,
    status: "idle",
    actionStatus: "idle",
    error: null,
    actionError: null,
    lastPlaced: null,
    activeHouseId: null,
  },
  reducers: {
    clearBettingDesk(state) {
      state.slip = null;
      state.status = "idle";
      state.actionStatus = "idle";
      state.error = null;
      state.actionError = null;
      state.lastPlaced = null;
      state.activeHouseId = null;
    },
    clearBettingDeskError(state) {
      state.error = null;
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBettingSlip.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        state.activeHouseId = action.meta.arg.betId;
      })
      .addCase(fetchBettingSlip.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.slip = action.payload;
      })
      .addCase(fetchBettingSlip.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(addSelectionToSlip.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(addSelectionToSlip.fulfilled, (state, action) => {
        state.actionStatus = "idle";
        state.slip = action.payload;
      })
      .addCase(addSelectionToSlip.rejected, (state, action) => {
        state.actionStatus = "idle";
        state.actionError = action.payload;
      })
      .addCase(removeSelectionFromSlip.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(removeSelectionFromSlip.fulfilled, (state, action) => {
        state.actionStatus = "idle";
        state.slip = action.payload;
      })
      .addCase(removeSelectionFromSlip.rejected, (state, action) => {
        state.actionStatus = "idle";
        state.actionError = action.payload;
      })
      .addCase(placeBetTicket.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(placeBetTicket.fulfilled, (state, action) => {
        state.actionStatus = "idle";
        state.lastPlaced = action.payload;
        state.slip = null;
      })
      .addCase(placeBetTicket.rejected, (state, action) => {
        state.actionStatus = "idle";
        state.actionError = action.payload;
      });
  },
});

export const { clearBettingDesk, clearBettingDeskError } = bettingDeskSlice.actions;
export default bettingDeskSlice.reducer;
